import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Eye, EyeOff } from "lucide-react";

/** What a hidden asmens kodas looks like until the viewer clicks the eye. */
export const MASKED_PERSONAL_CODE = "•••••••••••";

interface SensitiveTextProps {
  /** The text revealed by the eye; empty shows a dash and no eye. */
  text: string | null | undefined;
  /** Shown instead of the text while hidden. */
  mask: string;
  showLabel: string;
  hideLabel: string;
  className?: string;
}

/**
 * A sensitive value shown as `mask` until the viewer clicks the eye. Each value
 * has its own switch, and it is forgotten when the component unmounts, so
 * leaving the page hides the value again.
 */
export default function SensitiveText({ text, mask, showLabel, hideLabel, className }: SensitiveTextProps) {
  const [revealed, setRevealed] = useState(false);

  if (!text) return <span className={className}>—</span>;

  const label = revealed ? hideLabel : showLabel;
  const Icon = revealed ? EyeOff : Eye;

  return (
    <span className={clsx("inline-flex items-center gap-1.5", className)}>
      <span>{revealed ? text : mask}</span>
      <button
        type="button"
        onClick={() => setRevealed(!revealed)}
        aria-label={label}
        title={label}
        className="text-gray-400 hover:text-gray-700 max-md:-m-2.5 max-md:p-2.5"
      >
        <Icon size={14} />
      </button>
    </span>
  );
}

/** An asmens kodas behind the eye — the card header and the audit history. */
export function SensitivePersonalCode({ value, className }: { value: string | null | undefined; className?: string }) {
  const { t } = useTranslation();
  return (
    <SensitiveText
      text={value}
      mask={MASKED_PERSONAL_CODE}
      showLabel={t("show_personal_code")}
      hideLabel={t("hide_personal_code")}
      className={className}
    />
  );
}
